import { Box, Divider, Flex, Icon, Text, Tooltip, ReadMoreMarkdown } from '@fullstory/components';
import { TimeRange } from '@fullstory/proto/fs/api/segment';
import { TrendFrequency } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { SegmentDetail } from '@fullstory/proto/fs/services/dx-objects';
import React, { PropsWithChildren } from 'react';
import { RetentionTooltipData } from '../../hooks';
import { RollingWindow } from '../../types';
import { MetadataDisplay } from '../MetadataDisplay';
import { SegmentName } from '../SegmentName';
import { TimeRangeDisplay } from '../TimeRangeDisplay';
import { classNames } from '@fullstory/util/fmt';
import css from './index.module.scss';
import { FormattedMessage } from 'react-intl';
import { SortOrder, SortType } from '@fullstory/proto/fs/api/metrics';
import { SortDisplay } from './SortDisplay';

const pComponent = ({ ...props }) => (
  <Text appearance="bodySmall" tag="div" color="secondary">
    {props.children}
  </Text>
);

// copy of "multilineComponentsUnstyled" from markdown component but with secondary and bodySmall
const markdownComponents = {
  h1: ({ ...props }) => pComponent(props),
  h2: ({ ...props }) => pComponent(props),
  h3: ({ ...props }) => pComponent(props),
  h4: ({ ...props }) => pComponent(props),
  h5: ({ ...props }) => pComponent(props),
  h6: ({ ...props }) => pComponent(props),
  hr: ({ ...props }) => pComponent(props),
  ul: ({ ...props }) => pComponent(props),
  ol: ({ ...props }) => pComponent(props),
  li: ({ ...props }) => pComponent(props),
  p: ({ ...props }) => pComponent(props),
  code: ({ ...props }) => pComponent(props),
};

type SortConfig = {
  sortType: SortType;
  sortOrder: SortOrder;
  sortLabel: string;
};

export const LibraryObjectTooltip: React.FC<
  PropsWithChildren<{
    name: string;
    description?: string;
    metadata: React.ComponentProps<typeof MetadataDisplay>['metadata'] | null | undefined;
    icon: React.ComponentProps<typeof Icon>['name'];
    content: React.ReactNode | null;
    placement?: React.ComponentProps<typeof Tooltip>['placement'];
    segmentDetails?: SegmentDetail[];
    timeRange?: TimeRange | null;
    frequency?: TrendFrequency | null;
    rollingWindow?: RollingWindow;
    showDelay?: React.ComponentProps<typeof Tooltip>['showDelay'];
    className?: React.ComponentProps<typeof Tooltip>['className'];
    persistDelay?: React.ComponentProps<typeof Tooltip>['persistDelay'];
    onModifyOwner?: (() => void) | undefined;
    retentionTooltip: RetentionTooltipData | null;
    sortConfig?: SortConfig;
    actionComponent?: React.ReactNode;
    contentOnClick?: React.ComponentProps<typeof Box>['onClick'];
  }>
> = ({
  name,
  description,
  metadata,
  icon,
  content,
  placement = 'bottom-start',
  segmentDetails,
  timeRange,
  frequency,
  rollingWindow,
  onModifyOwner,
  showDelay = 100,
  className,
  children,
  retentionTooltip,
  sortConfig,
  persistDelay,
  actionComponent,
  contentOnClick,
}) => (
  <Tooltip
    data-component="LibraryObjectTooltip"
    maxWidth={400}
    showDelay={showDelay}
    variant="light"
    placement={placement}
    persistDelay={persistDelay}
    disablePadding
    tooltipContent={
      <Box maxWidth={400} overflow="hidden" onClick={contentOnClick}>
        <Box padding={[12, 16]} display="flex" justifyContent="space-between">
          <Flex justifyContent="flex-start">
            <Box marginRight={8}>
              <Icon name={icon} color="secondary" size={20} />
            </Box>
            <Text appearance="bodyMedium">{name}</Text>
          </Flex>
          {actionComponent}
        </Box>

        {(!!content ||
          (!!segmentDetails && segmentDetails.length > 0) ||
          !!timeRange ||
          !!description) && (
          <>
            <Divider />
            <Box padding={20} maxHeight="66vh" overflow="auto">
              {!!description && (
                <Flex column justifyContent="flex-start" alignItems="flex-start" gap={8}>
                  <Text appearance="bodySmallBold">
                    <FormattedMessage defaultMessage="Description" id="Q8Qw5B" />
                  </Text>
                  <Box width={360}>
                    <ReadMoreMarkdown
                      maxLines={2}
                      content={description}
                      components={markdownComponents}
                    />
                  </Box>
                </Flex>
              )}
              {content}
              {!!segmentDetails && segmentDetails.length > 0 && (
                <Box marginTop={24}>
                  <SegmentName segments={segmentDetails} variant="minimal" textVariant="dark" />
                </Box>
              )}
              {timeRange && (
                <Box marginTop={!!segmentDetails && segmentDetails.length > 0 ? 8 : 24}>
                  <TimeRangeDisplay
                    timeRange={timeRange}
                    frequency={frequency ?? null}
                    rollingWindow={rollingWindow}
                  />
                  {retentionTooltip?.text && (
                    <Box paddingTop={8}>
                      <Text appearance="bodySmall" color="secondary">
                        {retentionTooltip.text}
                      </Text>
                    </Box>
                  )}
                </Box>
              )}
              {sortConfig ? (
                <Box marginTop={8}>
                  <SortDisplay
                    sortType={sortConfig.sortType}
                    sortOrder={sortConfig.sortOrder}
                    aggregationLabel={sortConfig.sortLabel}
                  />
                </Box>
              ) : null}
            </Box>
          </>
        )}
        {metadata && <MetadataDisplay onModifyOwner={onModifyOwner} metadata={metadata} />}
      </Box>
    }
    className={classNames(css.topAncestorFlexChild, className)}
  >
    {children}
  </Tooltip>
);
LibraryObjectTooltip.displayName = 'LibraryObjectTooltip';
